import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";

/**
 * Formata um número de CPF (Cadastro de Pessoa Física).
 *
 * @param value - O número de CPF a ser formatado.
 * @returns O número de CPF formatado no formato XXX.XXX.XXX-XX.
 */
export const formatCPF = (value: string): string => {
  const numericValue = value.replace(/\D/g, "").slice(0, 11);
  // XXX.XXX.XXX-XX
  return numericValue
    .replace(/(?<temp2>\d{3})(?<temp1>\d)/, "$1.$2")
    .replace(/(?<temp2>\d{3})(?<temp1>\d)/, "$1.$2")
    .replace(/(?<temp2>\d{3})(?<temp1>\d{1,2})$/, "$1-$2");
};

/**
 * Formata um número para o formato monetário (X.XXX,XX).
 *
 * @param value - O número a ser formatado.
 * @returns O número formatado no formato monetário.
 */
export const maskCurrency = (value: string): string => {
  const onlyNumbers = value.replace(/\D/g, "").replace(/^0+/, "").padStart(3, "0");

  let integer = onlyNumbers.slice(0, -2);
  const decimal = onlyNumbers.slice(-2);

  integer = integer.replace(/(?:\d)(?=(?:\d{3})+(?!\d))/g, "$&.");

  return `${integer},${decimal}`;
};

/**
 * Formata um número para o formato numérico.
 *
 * @param value - O número a ser formatado.
 * @returns O número formatado no formato numérico.
 */
export const maskNumber = (value: string): number => {
  const onlyNumbers = value.replace(/\D/g, "").replace(/^0+/, "").padStart(1, "0");
  return Number(onlyNumbers);
};

/**
 * Valida um número de CPF (Cadastro de Pessoa Física).
 *
 * @param cpf - O número de CPF a ser validado.
 * @returns um booleano indicando se o CPF é válido ou não.
 */
export const validateCPFChecksum = (cpf: string): boolean => {
  const modifiedCpf = cpf.replace(/(?:[~!@#$%^&*()_+=`{}[\]\-|\\:;'<>,./? ])+/g, "");

  if (modifiedCpf.length !== 11) {
    return false;
  }
  let sum = 0,
    i: number;

  const firstVerifyingDigit = parseInt(modifiedCpf.substring(9, 10), 10);
  const secondVerifyingDigit = parseInt(modifiedCpf.substring(10, 11), 10);

  if (
    modifiedCpf === "" ||
    modifiedCpf === "00000000000" ||
    modifiedCpf === "11111111111" ||
    modifiedCpf === "22222222222" ||
    modifiedCpf === "33333333333" ||
    modifiedCpf === "44444444444" ||
    modifiedCpf === "55555555555" ||
    modifiedCpf === "66666666666" ||
    modifiedCpf === "77777777777" ||
    modifiedCpf === "88888888888" ||
    modifiedCpf === "99999999999"
  ) {
    return false;
  }

  for (i = 1; i <= 9; i++) {
    sum = sum + parseInt(modifiedCpf.substring(i - 1, i), 10) * (11 - i);
  }

  if (checkSumCpfResult(sum, firstVerifyingDigit)) {
    sum = 0;
    for (i = 1; i <= 10; i++) {
      sum = sum + parseInt(modifiedCpf.substring(i - 1, i), 10) * (12 - i);
    }
    return checkSumCpfResult(sum, secondVerifyingDigit);
  }
  return false;
};

/**
 * Verifica se soma dos números do CPF é igual ao número verificador do CPF
 *
 * @param sum - O número da soma dos 9 primeiros dígitos do CPF.
 * @param verifyingDigit - O penúltimo ou último dígito do CPF.
 * @returns um booleano indicando se a soma dos números do CPF é igual ao penúltimo ou último dígito do CPF.
 */
const checkSumCpfResult = (sum: number, verifyingDigit: number): boolean => {
  let result = (sum * 10) % 11;
  if (result === 10 || result === 11) {
    result = 0;
  }
  return result === verifyingDigit;
};

const trimEnd = (str: string, sequence: string): string => {
  let temp = str;
  while (temp.endsWith(sequence)) {
    temp = temp.substring(0, temp.length - sequence.length);
  }
  return temp;
};

const formatBrazilianPhone = (value: string): string => {
  const trimmed = value.trim();
  if (/^\(\d{2}\) \d{4,5}-\d{4}$/.test(trimmed)) {
    return trimmed;
  }

  const digits = trimmed.replace(/\D/g, "");

  if (digits.length === 11) {
    return digits.replace(
      /(?<ddd>\d{2})(?<prefixo>\d{5})(?<sufixo>\d{4})/,
      "($<ddd>) $<prefixo>-$<sufixo>",
    );
  }

  if (digits.length === 10) {
    return digits.replace(
      /(?<ddd>\d{2})(?<prefixo>\d{4})(?<sufixo>\d{4})/,
      "($<ddd>) $<prefixo>-$<sufixo>",
    );
  }

  return value;
};

const formatInternationalPhone = (value: string, phoneUtil: PhoneNumberUtil): string => {
  try {
    const phoneNumber = phoneUtil.parseAndKeepRawInput(value);

    if (phoneUtil.isPossibleNumber(phoneNumber)) {
      const isBrazilianPhoneNumber = phoneNumber.getCountryCode() === 55;

      if (isBrazilianPhoneNumber) {
        if (value.length < 14) {
          const digits = value.replace(/^\+?55/, "").replace(/\D/g, "");

          return digits.replace(
            /(?<ddd>\d{2})(?<prefixo>\d{4})(?<sufixo>\d{4})/,
            "($<ddd>) $<prefixo>-$<sufixo>",
          );
        }

        return phoneUtil.format(phoneNumber, PhoneNumberFormat.NATIONAL);
      }

      const countryCode = phoneNumber.getCountryCode();
      if (countryCode === 1) {
        return `+1 ${phoneUtil.format(phoneNumber, PhoneNumberFormat.NATIONAL)}`;
      }

      return phoneUtil.format(phoneNumber, PhoneNumberFormat.INTERNATIONAL);
    }

    const digits = value.replace(/\D/g, "");

    return digits.replace(
      /(?<ddd>\d{2})(?<prefixo>\d{5})(?<sufixo>\d{0,4})/,
      "($<ddd>) $<prefixo>-$<sufixo>",
    );
  } catch {
    return value.replace(/\D/g, "");
  }
};

const internalFormatPhoneNumber = (value: string): string => {
  const phoneUtil = PhoneNumberUtil.getInstance();

  const isBrazilianFormatted = !value.trim().startsWith("+");

  if (isBrazilianFormatted) {
    const formattedBrazilianPhoneNumber = formatBrazilianPhone(value);

    return formattedBrazilianPhoneNumber;
  }

  return formatInternationalPhone(value, phoneUtil);
};

/**
 * Formata um número de telefone removendo caracteres desnecessários.
 *
 * @param value - O número de telefone a ser formatado.
 * @returns O número de telefone formatado.
 */
export const formatPhoneNumber = (value: string): string => {
  const phone = internalFormatPhoneNumber(value);
  return trimEnd(trimEnd(trimEnd(phone, "-"), ")"), "(");
};

/**
 * Converte um número de telefone do formato (xx) xxxxx-xxxx para +55xxxxxxxxxxxx
 * @param phone - Número de telefone no formato (xx) xxxxx-xxxx
 * @returns Número formatado no formato +55xxxxxxxxxxxx
 */
export const formatPhoneToBrazilE164 = (phone: string): string => {
  // Remove todos os caracteres não numéricos
  const numericOnly = phone.replace(/\D/g, "");

  // Adiciona o prefixo internacional +55 (Brasil)
  return `+55${numericOnly}`;
};

/**
 * Retorna o telefone do paciente para preenchimento de formulários, respeitando o flag
 * de telefone internacional. Quando o flag está ativo e o paciente não possui telefone
 * internacional normalizado, assume que o telefone armazenado é brasileiro e o converte
 * para o formato E.164 (+55xxxxxxxxxxxx) — necessário porque o input internacional e o
 * schema esperam um número iniciando com `+`.
 */
export const getPrefilledPatientPhone = (
  telefone: string | null | undefined,
  telefoneInternacionalNormalizado: string | null | undefined,
  isInternationalPhoneActive: boolean,
): string => {
  if (!isInternationalPhoneActive) {
    return telefone ?? "";
  }
  if (telefoneInternacionalNormalizado) {
    return telefoneInternacionalNormalizado;
  }
  if (!telefone) return "";
  if (telefone.startsWith("+")) return telefone;
  return formatPhoneToBrazilE164(telefone);
};

/**
 * Garante que o campo `telefone` nunca seja enviado em formato internacional (+...).
 * Quando o telefone vem com `+`, ele é movido para `telefoneInternacionalNormalizado` e
 * `telefone` vai vazio — o backend deriva o formato nacional a partir do internacional.
 */
export const buildTelefonePayload = (
  telefone: string | null | undefined,
  telefoneInternacionalNormalizado: string | null | undefined,
): { telefone: string; telefoneInternacionalNormalizado?: string | null } => {
  if (telefone?.startsWith("+")) {
    return {
      telefone: "",
      telefoneInternacionalNormalizado: telefoneInternacionalNormalizado
        ? telefoneInternacionalNormalizado
        : telefone,
    };
  }

  return { telefone: telefone ?? "", telefoneInternacionalNormalizado };
};

/**
 * Valida um endereço de email.
 *
 * @param email - O endereço de email a ser validado.
 * @returns Verdadeiro se o endereço de email for válido, falso caso contrário.
 */
export const validateEmail = (email: string): boolean => {
  return (
    (/^(?<temp1>(?<temp2>[^<>()[\]\\.,;:\s@"]+(?<temp3>\.[^<>()[\]\\.,;:\s@"]+)*)|.(?<temp4>".+"))@(?<temp5>(?<temp6>\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(?<temp7>(?<temp8>[a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.exec(
      (email || "").toLowerCase(),
    )?.length ?? 0) > 0
  );
};

/**
 * Verifica se o número de cartão é da bandeira American Express (Amex).
 * Cartões Amex começam com 34 ou 37 e possuem 15 dígitos.
 *
 * @param value - O número do cartão (com ou sem espaços/formatação).
 * @returns true se for Amex, false caso contrário.
 */
export const isAmexCard = (value: string): boolean => {
  const numericValue = value.replace(/\D/g, "");
  return numericValue.startsWith("34") || numericValue.startsWith("37");
};

/**
 * Formata um número de cartão de crédito.
 * Para cartões Amex, usa o padrão 4-6-5 (XXXX XXXXXX XXXXX).
 * Para demais cartões, usa o padrão 4-4-4-4 (XXXX XXXX XXXX XXXX).
 *
 * @param value - O número do cartão a ser formatado.
 * @returns O número formatado de acordo com a bandeira.
 */
export const formatCreditCard = (value: string): string => {
  const numericValue = value.replace(/\D/g, "");

  if (isAmexCard(numericValue)) {
    const truncated = numericValue.slice(0, 15);
    if (truncated.length <= 4) return truncated;
    if (truncated.length <= 10) return `${truncated.slice(0, 4)} ${truncated.slice(4)}`;
    return `${truncated.slice(0, 4)} ${truncated.slice(4, 10)} ${truncated.slice(10)}`;
  }

  const truncated = numericValue.slice(0, 16);
  return truncated.replace(/(?<temp1>\d{4})(?=\d)/g, "$1 ");
};

/**
 * Formata a data de vencimento do cartão
 *
 * @param value - os dígitos numéricos da data de vencimento do cartão.
 * @returns O número formatado no formato XX/XX.
 */
export const formatCardExpireDate = (value: string): string => {
  const numericValue = value.replace(/\D/g, "").slice(0, 4);

  return numericValue.replace(/(?<mm>\d{2})(?<yy>\d{2})/, "$1/$2");
};

/**
 * Formata uma placa de veículo brasileira.
 * Aceita tanto o padrão antigo (ABC-1234) quanto o Mercosul (ABC1D23).
 *
 * @param value - A placa a ser formatada.
 * @returns A placa formatada em maiúsculas, apenas letras e números.
 */
export const formatVehiclePlate = (value: string): string => {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 7);
};

/**
 * Valida uma placa de veículo brasileira.
 * Aceita tanto o padrão antigo (ABC1234) quanto o Mercosul (ABC1D23).
 *
 * @param plate - A placa a ser validada.
 * @returns Verdadeiro se a placa for válida, falso caso contrário.
 */
export const validateVehiclePlate = (plate: string): boolean => {
  const cleanPlate = plate.toUpperCase().replace(/[^A-Z0-9]/g, "");

  if (cleanPlate.length !== 7) {
    return false;
  }

  // Padrão antigo: ABC1234 (3 letras + 4 números)
  const oldPattern = /^[A-Z]{3}[0-9]{4}$/;

  // Padrão Mercosul: ABC1D23 (3 letras + 1 número + 1 letra + 2 números)
  const mercosulPattern = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/;

  return oldPattern.test(cleanPlate) || mercosulPattern.test(cleanPlate);
};
